import { Injectable } from '@angular/core';
import { readContract, reconnect, signMessage, writeContract } from "@wagmi/core";
import { wagmiConfig } from '../config/wagmi-config';
import { environment } from '../../../environments/environment';
import { encodeFunctionData } from 'viem';
import { getAccount, getChainId } from '@wagmi/core';
import { ContractType } from '../../shared/enums/common.enum';

const multisigAbi = require('../../contracts/multisigAbi.json');
const aocTokenAbi = require('../../contracts/aocTokenAbi.json');
const bulkMintAbi = require('../../contracts/bulkMintAbi.json');

@Injectable({
  providedIn: 'root'
})
export class ContractService {

  constructor() { }

  /**
   * Reads contract function
   * @param abi 
   * @param address 
   * @param functionName 
   * @param [args] 
   * @returns  
   */
    protected async readContractFunction(abi: any, address: any, functionName: string, args: any[] = []) {  
      const read = await readContract(wagmiConfig, {
        abi: abi,
        address: address,
        functionName: functionName,
        args: args
      });
      return read;
    }

  /**
   * Writes contract function
   * @param abi 
   * @param address 
   * @param functionName 
   * @param [args] 
   * @returns  
   */
  protected async writeContractFunction(abi: any, address: any, functionName: string, args: any[] = []) {
    // Ensure wallet connection is alive
    let account = getAccount(wagmiConfig);

    if (!account.isConnected) {
      console.warn('Wallet not connected, attempting reconnect...');
      await reconnect(wagmiConfig); // tries to restore the last session
      account = getAccount(wagmiConfig);
    }

    // Ensure chainId is fetched from wagmi core
    const chainId = getChainId(wagmiConfig);
    console.log(`Connected to chain ${chainId}, address: ${account.address}`);

    if (!account.address) throw new Error('No wallet address found.');
    const result = await writeContract(wagmiConfig, {
      abi: abi,
      address: address,
      functionName: functionName,
      args: args
    });
    return result;
  }

  /**
   * Determines whether blacklisted is
   * @param address
   * @returns
   */
  async isBlacklisted(address: any) {
    return await this.readContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'blacklisted', [address])
  }

  /**
   * Determines whether excluded from rams is
   * @param address
   * @returns
   */
  async isExcludedFromRAMS(address: any) {
    return await this.readContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'excludedFromRAMS', [address])
  }

  /**
   * Determines whether included in ltaf is
   * @param address
   * @returns
   */
  async isIncludedInLTAF(address: any) {
    return await this.readContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'includedInLTAF', [address])
  }

  /**
   * Blacklists function
   * @param address
   */
  async blacklistFunction(address: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'blacklistUser', [address])
  }

  /**
   * Removes blacklist function
   * @param address
   */
  async removeBlacklistFunction(address: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'removeFromBlacklist', [address])
  }

  /**
   * Includes ramsfunction
   * @param address
   */
  async includeRAMSFunction(address: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'includeInRAMS', [address])
  }

  /**
   * Excludes ramsfunction
   * @param address
   */
  async excludeRAMSFunction(address: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'excludeFromRAMS', [address])
  }

  /**
   * Includes ltaffunction
   * @param address
   */
  async includeLTAFFunction(address: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'includeInLTAF', [address])
  }

  /**
   * Excludes ltaffunction
   * @param address
   */
  async excludeLTAFFunction(address: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'excludedFromLTAF', [address])
  }

  /**
   * Percantages ltaffunction
   * @param percentage
   */
  async percantageLTAFFunction(percentage: any) {
    await this.writeContractFunction(aocTokenAbi, environment.AOC_TOKEN_CONTRACT, 'updateLtafPercentage', [percentage])
  }

  /**
   * Gets approval count
   * @param address
   * @returns
   */
  async getApprovalCount(address: string) {
    const count = await this.readContractFunction(multisigAbi, address, 'requiredApprovals', []);
    return Number(count);

  }

  /**
   * Signs message
   * @param walletAddress
   * @param message
   * @returns
   */

  async signMessage(walletAddress: string, message: string) {
    try {
      // Step 1: Get current account and chain info
      let account = getAccount(wagmiConfig);
      let chainId: number | null = null;
  
      // Step 2: Check if connector is valid
      const connector = account?.connector as any;
      if (!connector || typeof connector.getChainId !== 'function') {
        console.warn('Connector not initialized — reconnecting...');
        await reconnect(wagmiConfig); // Force Wagmi to rebuild the connector
        account = getAccount(wagmiConfig);
      }
  
      // Step 3: Safely fetch chain ID
      try {
        chainId = getChainId(wagmiConfig);
      } catch (e) {
        console.warn('Chain ID fetch failed, reinitializing...');
        await reconnect(wagmiConfig);
        chainId = getChainId(wagmiConfig);
      }
  
      console.log('Signing with account:', account, 'on chain:', chainId);
  
      // Step 4: Sign message
      return await signMessage(wagmiConfig, {
        account: walletAddress as `0x${string}`,
        message: message
      });
  
    } catch (err) {
      console.error('Error signing message:', err);
      throw err;
    }
  }

  async encodedDataFunction(type: any, functionName: string, args: any[] = []) {
    const abi = type === ContractType.AOC_TOKEN ? aocTokenAbi : bulkMintAbi;    
    return await encodeFunctionData({
      abi,
      functionName: functionName,
      args: args
    });
  }

  async proposeTransactions(contractAddress: string, address: string, data: any, value: any, description: string) {
    console.log('proposeTransactions', address, data, value, description);
    
    return await this.writeContractFunction(multisigAbi, contractAddress, 'proposeTransaction', [address, data, value, description]);
  }

  async approveTx(address: string, txId:any) {
    return await this.writeContractFunction(multisigAbi, address, 'approveTransaction', [txId])
  }
  
}
